import { useCallback,useEffect,useMemo,useState } from "react";
import {
  Box,Typography,Button,CircularProgress,TextField,FormControl,InputLabel,Select,MenuItem,
  Switch,Table,TableBody,TableCell,TableContainer,TableHead,TableRow,Dialog,DialogTitle,
  DialogContent,DialogActions
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useAppTheme } from "../../../context/ThemeContext";
import { useAlert } from "../../../context/AlertContext";
import PhaseConfiguration from "./TournamentPhases/PhaseConfiguration";
import MatchSchedule from "./TournamentPhases/MatchSchedule";

const getCookie=(name)=>{
  const value=document.cookie.split("; ").find((row)=>row.startsWith(`${name}=`));
  return value?decodeURIComponent(value.split("=")[1]):"";
};

const readResponse=async(response)=>{
  const contentType=response.headers.get("content-type")||"";
  if(contentType.includes("application/json")) return response.json();
  if(!response.ok) throw new Error("El servidor no respondió correctamente.");
  return {};
};

const emptyPhase={
  name:"",
  format:"LEAGUE",
  round_trip:false,
  teams_advance:"",
  active:true
};

const formatName=(value)=>{
  if(value==="LEAGUE") return "Todos contra todos";
  if(value==="GROUPS") return "Fase de grupos";
  if(value==="KNOCKOUT") return "Eliminación directa";
  return value||"—";
};

export default function TournamentPhases(){
  const {slug,categorySlug}=useParams();
  const {colors,typography,shape}=useAppTheme();
  const {showSuccess,showError}=useAlert();

  const [phases,setPhases]=useState([]);
  const [loading,setLoading]=useState(true);
  const [loadingDetails,setLoadingDetails]=useState(false);
  const [activeSection,setActiveSection]=useState("information");

  const [phaseDialog,setPhaseDialog]=useState(false);
  const [editingPhase,setEditingPhase]=useState(null);
  const [phaseForm,setPhaseForm]=useState(emptyPhase);
  const [savingPhase,setSavingPhase]=useState(false);

  const [groups,setGroups]=useState({});
  const [phaseTeams,setPhaseTeams]=useState({});

  const sections=[
    {key:"information",label:"Información"},
    {key:"schedule",label:"Calendario de partidos"}
  ];

  const loadGroupsRequest=useCallback(async(phase)=>{
    if(phase.format!=="GROUPS") return [];

    const response=await fetch(
      `/tournaments/${slug}/categories/${categorySlug}/phases/${phase.slug}/groups/`,
      {credentials:"include"}
    );

    const data=await readResponse(response);

    if(!response.ok){
      throw new Error(data.error||data.detail||`No se pudieron cargar los grupos de ${phase.name}.`);
    }

    return Array.isArray(data)?data:data.results||[];
  },[slug,categorySlug]);

  const loadTeamsRequest=useCallback(async(phase)=>{
    const response=await fetch(
      `/tournaments/${slug}/categories/${categorySlug}/phases/${phase.slug}/teams/`,
      {credentials:"include"}
    );

    const data=await readResponse(response);

    if(!response.ok){
      throw new Error(data.error||data.detail||`No se pudieron cargar los equipos de ${phase.name}.`);
    }

    const rows=Array.isArray(data)?data:data.results||[];

    return rows.map((item)=>({
      ...item,
      selected:Boolean(item.assigned??item.active),
      group:item.group?.slug||item.group||"",
      seed:item.seed??""
    }));
  },[slug,categorySlug]);

  const loadAllDetails=useCallback(async(phaseList)=>{
    if(!phaseList.length){
      setGroups({});
      setPhaseTeams({});
      return;
    }

    setLoadingDetails(true);

    try{
      const results=await Promise.all(
        phaseList.map(async(phase)=>{
          const [phaseGroups,teams]=await Promise.all([
            phase.format==="GROUPS"?loadGroupsRequest(phase):Promise.resolve([]),
            loadTeamsRequest(phase)
          ]);

          return{
            phase,
            groups:phaseGroups,
            teams
          };
        })
      );

      const groupsMap={};
      const teamsMap={};

      results.forEach((result)=>{
        groupsMap[result.phase.slug]=result.groups;
        teamsMap[result.phase.slug]=result.teams;
      });

      setGroups(groupsMap);
      setPhaseTeams(teamsMap);
    }catch(error){
      showError(error.message||"No se pudo cargar la configuración de las fases.");
    }finally{
      setLoadingDetails(false);
    }
  },[loadGroupsRequest,loadTeamsRequest,showError]);

  const loadPhases=useCallback(async()=>{
    if(!slug||!categorySlug) return;

    try{
      setLoading(true);

      const response=await fetch(
        `/tournaments/${slug}/categories/${categorySlug}/phases/`,
        {credentials:"include"}
      );

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(data.error||data.detail||"No se pudieron cargar las fases.");
      }

      const phaseList=Array.isArray(data)?data:data.results||[];

      setPhases(phaseList);
      await loadAllDetails(phaseList);
    }catch(error){
      showError(error.message||"No se pudieron cargar las fases.");
    }finally{
      setLoading(false);
    }
  },[slug,categorySlug,loadAllDetails,showError]);

  useEffect(()=>{
    loadPhases();
  },[loadPhases]);

  const openNewPhase=()=>{
    setEditingPhase(null);
    setPhaseForm(emptyPhase);
    setPhaseDialog(true);
  };

  const openEditPhase=(phase)=>{
    setEditingPhase(phase);

    setPhaseForm({
      name:phase.name||"",
      format:phase.format||"LEAGUE",
      round_trip:Boolean(phase.round_trip),
      teams_advance:phase.format==="LEAGUE"?(phase.teams_advance??""):"",
      active:phase.active!==false
    });

    setPhaseDialog(true);
  };

  const closePhaseDialog=()=>{
    if(savingPhase) return;
    setPhaseDialog(false);
    setEditingPhase(null);
    setPhaseForm(emptyPhase);
  };

  const savePhase=async()=>{
    if(!phaseForm.name.trim()){
      showError("El nombre de la fase es obligatorio.");
      return;
    }

    let teamsAdvance=null;

    if(phaseForm.format==="LEAGUE"&&phaseForm.teams_advance!==""){
      teamsAdvance=Number(phaseForm.teams_advance);

      if(!Number.isInteger(teamsAdvance)||teamsAdvance<1){
        showError("Los equipos que avanzan deben ser un número válido.");
        return;
      }
    }

    try{
      setSavingPhase(true);

      const editing=Boolean(editingPhase?.slug);

      const url=editing
        ?`/tournaments/${slug}/categories/${categorySlug}/phases/${editingPhase.slug}/`
        :`/tournaments/${slug}/categories/${categorySlug}/phases/`;

      const response=await fetch(url,{
        method:editing?"PATCH":"POST",
        credentials:"include",
        headers:{
          "Content-Type":"application/json",
          "X-CSRFToken":getCookie("csrftoken")
        },
        body:JSON.stringify({
          name:phaseForm.name.trim(),
          format:phaseForm.format,
          round_trip:phaseForm.round_trip,
          teams_advance:phaseForm.format==="LEAGUE"?teamsAdvance:null,
          active:phaseForm.active
        })
      });

      const data=await readResponse(response);

      if(!response.ok){
        throw new Error(data.error||data.detail||"No se pudo guardar la fase.");
      }

      setPhaseDialog(false);
      setEditingPhase(null);
      setPhaseForm(emptyPhase);

      await loadPhases();

      showSuccess(editing?"Fase actualizada correctamente.":"Fase creada correctamente.");
    }catch(error){
      showError(error.message||"No se pudo guardar la fase.");
    }finally{
      setSavingPhase(false);
    }
  };

  const updatePhaseGroups=(phaseSlug,value)=>{
    setGroups((current)=>({
      ...current,
      [phaseSlug]:value
    }));
  };

  const updatePhaseTeams=(phaseSlug,value)=>{
    setPhaseTeams((current)=>({
      ...current,
      [phaseSlug]:value
    }));
  };

  const phaseTitle=useMemo(()=>{
    return categorySlug
      ?categorySlug.replaceAll("-"," ").toUpperCase()
      :"";
  },[categorySlug]);

  if(loading){
    return(
      <Box sx={{minHeight:300,display:"flex",alignItems:"center",justifyContent:"center"}}>
        <CircularProgress size={28} sx={{color:colors.secondary}} />
      </Box>
    );
  }

  return(
    <Box sx={{width:"100%",maxWidth:1300,mx:"auto"}}>
      <Box
        sx={{
          mb:2,
          display:"flex",
          flexDirection:{xs:"column",sm:"row"},
          alignItems:{xs:"stretch",sm:"center"},
          justifyContent:"space-between",
          gap:1
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize:{xs:typography.sizes.xl,md:typography.sizes.title},
              fontWeight:700,
              color:colors.text
            }}
          >
            Fases
          </Typography>

          <Typography
            sx={{
              mt:0.4,
              fontSize:typography.sizes.md,
              color:colors.textSecondary
            }}
          >
            Configura las fases, equipos participantes y calendario de partidos de {phaseTitle}.
          </Typography>
        </Box>

        {activeSection==="information"&&(
          <Button
            size="small"
            variant="contained"
            onClick={openNewPhase}
            sx={{
              px:2,
              textTransform:"none",
              backgroundColor:colors.secondary,
              "&:hover":{backgroundColor:colors.secondaryHover}
            }}
          >
            Nueva fase
          </Button>
        )}
      </Box>

      <Box
        sx={{
          mb:2,
          display:"flex",
          flexWrap:"wrap",
          gap:0.5,
          p:0.5,
          border:`1px solid ${colors.border}`,
          borderRadius:shape?.radius||"10px",
          backgroundColor:colors.surface
        }}
      >
        {sections.map((section)=>{
          const active=activeSection===section.key;

          return(
            <Button
              key={section.key}
              size="small"
              onClick={()=>setActiveSection(section.key)}
              sx={{
                px:1.5,
                minHeight:36,
                borderRadius:shape?.radiusSmall||"7px",
                color:active?colors.secondary:colors.textSecondary,
                backgroundColor:active?colors.backgroundSoft:"transparent",
                textTransform:"none",
                fontWeight:active?600:500,
                "&:hover":{
                  color:colors.secondary,
                  backgroundColor:colors.backgroundSoft
                }
              }}
            >
              {section.label}
            </Button>
          );
        })}
      </Box>

      {loadingDetails&&(
        <Box
          sx={{
            mb:1.5,
            display:"flex",
            alignItems:"center",
            gap:1,
            color:colors.textSecondary
          }}
        >
          <CircularProgress size={18} sx={{color:colors.secondary}} />

          <Typography sx={{fontSize:typography.sizes.sm}}>
            Cargando configuración de las fases...
          </Typography>
        </Box>
      )}

      {phases.length===0?(
        <TableContainer
          sx={{
            border:`1px solid ${colors.border}`,
            borderRadius:"10px",
            backgroundColor:colors.surface,
            boxShadow:"none"
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{backgroundColor:colors.tableHeader}}>
                <TableCell sx={{color:colors.tableHeaderText,fontWeight:700}}>
                  Fases del torneo
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              <TableRow>
                <TableCell
                  align="center"
                  sx={{
                    py:4,
                    color:colors.textSecondary
                  }}
                >
                  Esta categoría todavía no tiene fases configuradas.
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ):(
        <>
          {activeSection==="information"&&(
            <Box sx={{display:"flex",flexDirection:"column",gap:2}}>
              {phases.map((phase)=>(
                <Box key={phase.slug}>
                  <TableContainer
                    sx={{
                      border:`1px solid ${colors.border}`,
                      borderRadius:"10px",
                      backgroundColor:colors.surface,
                      boxShadow:"none",
                      overflow:"hidden"
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{backgroundColor:colors.tableHeader}}>
                          <TableCell sx={{color:colors.tableHeaderText,fontWeight:700}}>
                            Fase
                          </TableCell>

                          <TableCell sx={{color:colors.tableHeaderText,fontWeight:700}}>
                            Formato
                          </TableCell>

                          <TableCell
                            align="center"
                            sx={{
                              color:colors.tableHeaderText,
                              fontWeight:700
                            }}
                          >
                            Ida y vuelta
                          </TableCell>

                          {phase.format==="LEAGUE"&&(
                            <TableCell
                              align="center"
                              sx={{
                                color:colors.tableHeaderText,
                                fontWeight:700
                              }}
                            >
                              Avanzan
                            </TableCell>
                          )}

                          <TableCell
                            align="center"
                            sx={{
                              color:colors.tableHeaderText,
                              fontWeight:700
                            }}
                          >
                            Activa
                          </TableCell>

                          <TableCell
                            align="right"
                            sx={{
                              color:colors.tableHeaderText,
                              fontWeight:700
                            }}
                          >
                            Acciones
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        <TableRow>
                          <TableCell sx={{fontWeight:600,color:colors.text}}>
                            {phase.name}
                          </TableCell>

                          <TableCell sx={{color:colors.text}}>
                            {formatName(phase.format)}
                          </TableCell>

                          <TableCell align="center" sx={{color:colors.text}}>
                            {phase.round_trip?"Sí":"No"}
                          </TableCell>

                          {phase.format==="LEAGUE"&&(
                            <TableCell align="center" sx={{color:colors.text}}>
                              {phase.teams_advance??"—"}
                            </TableCell>
                          )}

                          <TableCell align="center" sx={{color:colors.text}}>
                            {phase.active?"Sí":"No"}
                          </TableCell>

                          <TableCell align="right">
                            <Button
                              size="small"
                              onClick={()=>openEditPhase(phase)}
                              sx={{
                                minWidth:0,
                                textTransform:"none",
                                color:colors.secondary
                              }}
                            >
                              Editar
                            </Button>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <PhaseConfiguration
                    tournamentSlug={slug}
                    categorySlug={categorySlug}
                    phase={phase}
                    groups={groups[phase.slug]||[]}
                    teams={phaseTeams[phase.slug]||[]}
                    onGroupsChange={(value)=>updatePhaseGroups(phase.slug,value)}
                    onTeamsChange={(value)=>updatePhaseTeams(phase.slug,value)}
                  />
                </Box>
              ))}
            </Box>
          )}

          {activeSection==="schedule"&&(
            <Box sx={{display:"flex",flexDirection:"column",gap:2}}>
              {phases.map((phase)=>(
                <Box key={phase.slug}>
                  <Box sx={{mb:0.8}}>
                    <Typography
                      sx={{
                        fontSize:typography.sizes.md,
                        fontWeight:700,
                        color:colors.text
                      }}
                    >
                      {phase.name}
                    </Typography>

                    <Typography
                      sx={{
                        mt:0.2,
                        fontSize:typography.sizes.sm,
                        color:colors.textSecondary
                      }}
                    >
                      {formatName(phase.format)}
                    </Typography>
                  </Box>

                  <MatchSchedule
                    tournamentSlug={slug}
                    categorySlug={categorySlug}
                    phase={phase}
                    groups={groups[phase.slug]||[]}
                    teams={phaseTeams[phase.slug]||[]}
                  />
                </Box>
              ))}
            </Box>
          )}
        </>
      )}

      <Dialog
        open={phaseDialog}
        onClose={closePhaseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          <Typography
            sx={{
              fontSize:typography.sizes.xl,
              fontWeight:700,
              color:colors.text
            }}
          >
            {editingPhase?"Editar fase":"Nueva fase"}
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box
            sx={{
              pt:1,
              display:"flex",
              flexDirection:"column",
              gap:1.5
            }}
          >
            <TextField
              size="small"
              label="Nombre de la fase"
              value={phaseForm.name}
              onChange={(event)=>
                setPhaseForm((current)=>({
                  ...current,
                  name:event.target.value
                }))
              }
              fullWidth
              autoFocus
            />

            <FormControl size="small" fullWidth>
              <InputLabel>Formato</InputLabel>

              <Select
                value={phaseForm.format}
                label="Formato"
                onChange={(event)=>{
                  const format=event.target.value;

                  setPhaseForm((current)=>({
                    ...current,
                    format,
                    teams_advance:format==="LEAGUE"
                      ?current.teams_advance
                      :""
                  }));
                }}
              >
                <MenuItem value="LEAGUE">
                  Todos contra todos
                </MenuItem>

                <MenuItem value="GROUPS">
                  Fase de grupos
                </MenuItem>

                <MenuItem value="KNOCKOUT">
                  Eliminación directa
                </MenuItem>
              </Select>
            </FormControl>

            {phaseForm.format==="LEAGUE"&&(
              <TextField
                size="small"
                label="Equipos que avanzan"
                type="number"
                value={phaseForm.teams_advance}
                onChange={(event)=>
                  setPhaseForm((current)=>({
                    ...current,
                    teams_advance:event.target.value
                  }))
                }
                inputProps={{min:1}}
                fullWidth
              />
            )}

            {phaseForm.format==="GROUPS"&&(
              <Typography
                sx={{
                  px:0.3,
                  fontSize:typography.sizes.sm,
                  color:colors.textSecondary
                }}
              >
                Los equipos que avanzan se configuran individualmente en cada grupo.
              </Typography>
            )}

            <Box
              sx={{
                display:"flex",
                alignItems:"center",
                justifyContent:"space-between",
                border:`1px solid ${colors.border}`,
                borderRadius:"8px",
                px:1.5,
                py:0.7
              }}
            >
              <Typography
                sx={{
                  fontSize:typography.sizes.md,
                  color:colors.text
                }}
              >
                Ida y vuelta
              </Typography>

              <Switch
                size="small"
                checked={phaseForm.round_trip}
                onChange={(event)=>
                  setPhaseForm((current)=>({
                    ...current,
                    round_trip:event.target.checked
                  }))
                }
              />
            </Box>

            <Box
              sx={{
                display:"flex",
                alignItems:"center",
                justifyContent:"space-between",
                border:`1px solid ${colors.border}`,
                borderRadius:"8px",
                px:1.5,
                py:0.7
              }}
            >
              <Typography
                sx={{
                  fontSize:typography.sizes.md,
                  color:colors.text
                }}
              >
                Fase activa
              </Typography>

              <Switch
                size="small"
                checked={phaseForm.active}
                onChange={(event)=>
                  setPhaseForm((current)=>({
                    ...current,
                    active:event.target.checked
                  }))
                }
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button
            size="small"
            disabled={savingPhase}
            onClick={closePhaseDialog}
            sx={{
              textTransform:"none",
              color:colors.textSecondary
            }}
          >
            Cancelar
          </Button>

          <Button
            size="small"
            variant="contained"
            disabled={savingPhase}
            onClick={savePhase}
            sx={{
              textTransform:"none",
              backgroundColor:colors.secondary,
              "&:hover":{backgroundColor:colors.secondaryHover}
            }}
          >
            {savingPhase?"Guardando...":"Guardar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}